import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  GridItem,
  Heading,
  Link,
  List,
  ListItem,
  SimpleGrid,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { type Document } from '@contentful/rich-text-types'
import NextLink from 'next/link'
import ContentfulImage from '~/components/shared-components/ContentfulImage/ContentfulImage'
import ContentfulImageButton from '~/components/shared-components/ContentfulImageButton/ContentfulImageButton'
import DocumentToReactRender, {
  type EntryId,
} from '~/components/shared-components/DocumentToReactRender/DocumentToReactRender'
import { useNavigation } from '~/context/NavigationProvider'
import { pxToRem } from '~/utils/pxToRem'

import {
  type IReducedAsset,
  type IReducedButton,
  type IReducedNavigationElement,
} from '../../../types/reducedTypes'
import Container from '../../grid/Container/Container'
import ContentfulButton from '../../section-modules/ContentfulButton/ContentfulButton'

export interface FooterProps {
  id: EntryId
  appText: string
  appImages: IReducedNavigationElement[]
  socialMediaText: string
  socialMediaImages: IReducedNavigationElement[]
  lists: {
    id: EntryId
    title: string
    pages: {
      id: EntryId
      text: string
    }[]
    externalLinks: {
      id: EntryId
      text: string
      url: string
    }[]
  }[]
  additionalButton?: IReducedButton
  legalText?: Document
  extraText?: Document
  awardImages: IReducedAsset[]
}

export default function Footer(props: FooterProps) {
  const { findFullUrlFromId } = useNavigation()

  const desktopBreakpoint = 'lg'
  const gridGap = '32px'

  const DesktopFooterMenu = () => (
    <SimpleGrid className="desktop-footer-menu" columns={12} gap={gridGap}>
      {props.lists.map((list, index) => {
        return (
          <GridItem
            key={`${list.id}-${index}`}
            colSpan={2}
            colStart={{
              [desktopBreakpoint]: index === 0 ? 2 : undefined,
            }}
          >
            <Text
              fontSize={pxToRem(18)}
              fontWeight="400"
              textTransform="uppercase"
              marginBottom="30px"
            >
              {list.title}
            </Text>

            <List spacing="16px">
              {list.pages.map((page, index) => (
                <ListItem key={`${page.id}-${index}`}>
                  <Link
                    as={NextLink}
                    href={`/${findFullUrlFromId(page.id) ?? ''}`}
                  >
                    {page.text}
                  </Link>
                </ListItem>
              ))}

              {list.externalLinks.map((link, index) => (
                <ListItem key={`${link.id}-${index}`}>
                  <Link
                    as={NextLink}
                    href={link.url ?? ''}
                    target="_blank"
                    rel="noopener"
                  >
                    {link.text}
                  </Link>
                </ListItem>
              ))}
            </List>
          </GridItem>
        )
      })}
    </SimpleGrid>
  )

  const MobileFooterMenu = () => {
    return (
      <Flex
        className="mobile-footer-menu"
        direction="column"
        w="100%"
        px="14px"
      >
        <Accordion allowToggle variant="navStyles">
          {props.lists.map((list, index) => (
            <AccordionItem key={`${list.id}-${index}`}>
              <AccordionButton>
                {list.title}
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel>
                <Flex direction="column" gap="24px">
                  <List spacing="24px">
                    {list.pages.map((page, index) => (
                      <ListItem key={`${page.id}-${index}`}>
                        <Link
                          as={NextLink}
                          href={`/${findFullUrlFromId(page.id) ?? ''}`}
                        >
                          {page.text}
                        </Link>
                      </ListItem>
                    ))}

                    {list.externalLinks?.map((link, index) => (
                      <ListItem key={`${link.id}-${index}`}>
                        <Link
                          as={NextLink}
                          href={link.url ?? ''}
                          target="_blank"
                          rel="noopener"
                        >
                          {link.text}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Flex>
    )
  }

  const FooterMenu = useBreakpointValue({
    base: <MobileFooterMenu />,
    [desktopBreakpoint]: <DesktopFooterMenu />,
  })

  const FooterHighlight = () => (
    <SimpleGrid
      columns={{ [desktopBreakpoint]: 5 }}
      gap={{ base: '37px' }}
      position="relative"
      zIndex="2"
      p={{ base: '62px 0', lg: '60px 0' }}
    >
      <GridItem
        colStart={{ [desktopBreakpoint]: 2 }}
        colSpan={{ [desktopBreakpoint]: 4 }}
      >
        {props.extraText && (
          <DocumentToReactRender
            componentData={props.extraText}
            sx={{
              color: 'primary.darkGreen.100',
              p: {
                fontWeight: {
                  base: 300,
                  [desktopBreakpoint]: 325,
                },
                fontSize: pxToRem(12),
                lineHeight: '141.667%',
              },
            }}
            fieldId="extraText"
            entryId={props.id}
          />
        )}
      </GridItem>

      {(props.appImages || props.socialMediaImages) && (
        <GridItem
          colStart={{ [desktopBreakpoint]: 2 }}
          colSpan={{ [desktopBreakpoint]: 4 }}
        >
          <Flex direction="column" gap={{ base: '37px' }}>
            {props.appImages && !!props.appImages.length && (
              <Flex direction="column" gap={{ base: '26px', lg: '34px' }}>
                <Heading
                  as="h3"
                  variant="subheading"
                  color="primary.darkGreen.100"
                >
                  {props.appText}
                </Heading>

                <Flex
                  justifyContent={{ base: 'flex-start', sm: 'flex-start' }}
                  w="100%"
                  gap="16px"
                  wrap="wrap"
                >
                  {props.appImages?.map((appImage, index) => (
                    <ContentfulImageButton
                      key={`${appImage.sys.id}-${index}`}
                      componentData={appImage}
                      overrideValues={{ height: 52 }}
                      buttonProps={{ maxH: '52px', maxW: 'max-content' }}
                      fieldId="image"
                    />
                  ))}
                </Flex>
              </Flex>
            )}

            {props.socialMediaImages && !!props.socialMediaImages.length && (
              <Flex
                direction="column"
                gap={{ base: '26px', [desktopBreakpoint]: '34px' }}
              >
                <Heading
                  as="h3"
                  variant="subheading"
                  color="primary.darkGreen.100"
                >
                  {props.socialMediaText}
                </Heading>

                <Flex w="100%" gap="16px" flexWrap="wrap">
                  {props.socialMediaImages?.map((socialImage, index) => (
                    <ContentfulImageButton
                      key={`${socialImage.sys.id}-${index}`}
                      overrideValues={{ height: 52 }}
                      componentData={socialImage}
                      buttonProps={{ maxH: '52px', maxW: 'min-content' }}
                      fieldId="image"
                    />
                  ))}
                </Flex>
              </Flex>
            )}

            {props.additionalButton && (
              <ContentfulButton buttonData={props.additionalButton} />
            )}
          </Flex>
        </GridItem>
      )}
    </SimpleGrid>
  )

  return (
    <Flex className="footer" direction="column" bg="primary.teal.15">
      <Box
        bg="primary.white.100"
        py={{ base: '30px', [desktopBreakpoint]: '32px' }}
      >
        <Container>
          <Button
            as="a"
            href="#top"
            variant="outlineDark"
            m={{ base: '0 auto 20px', [desktopBreakpoint]: '0 0 30px auto' }}
          >
            Back to top
          </Button>
          <nav aria-label="Footer">{FooterMenu}</nav>
        </Container>
      </Box>

      <Box overflow="hidden">
        <Container>
          <SimpleGrid
            columns={{ base: 1, [desktopBreakpoint]: 12 }}
            gap={{ base: 0, [desktopBreakpoint]: gridGap }}
          >
            <GridItem
              colSpan={{ base: 1, [desktopBreakpoint]: 5 }}
              order={{ [desktopBreakpoint]: 2 }}
              position="relative"
            >
              {/* Element to place behind the right-hand section for the background */}
              <Box
                background="primary.teal.60"
                position="absolute"
                top="0"
                width="200vw"
                left={{ base: '-16px', sm: '-100vw', [desktopBreakpoint]: 0 }}
                bottom="0"
                zIndex={1}
              />
              <FooterHighlight />
            </GridItem>

            <GridItem
              colSpan={{ base: 1, [desktopBreakpoint]: 6 }}
              order={{ [desktopBreakpoint]: 1 }}
              colStart={{ [desktopBreakpoint]: 2 }}
            >
              <Flex
                p={{
                  base: '27px 0 60px',
                  md: '60px 0 40px',
                  lg: '60px 0 40px',
                }}
              >
                <Flex flexDirection="column" gap={gridGap}>
                  {props.legalText && (
                    <DocumentToReactRender
                      componentData={props.legalText}
                      sx={{
                        p: {
                          fontWeight: {
                            base: 300,
                            [desktopBreakpoint]: 325,
                          },
                          fontSize: pxToRem(12),
                          lineHeight: '141.667%',
                        },
                      }}
                      fieldId="legalText"
                      entryId={props.id}
                    />
                  )}

                  <Flex wrap="wrap" alignItems="center" gap="20px">
                    {props.awardImages?.map((image, index) => {
                      return (
                        <Box
                          key={`${image.sys.id}-${index}`}
                          filter="grayscale(100%)"
                        >
                          <ContentfulImage
                            image={image}
                            overrideValues={{
                              height: 100,
                            }}
                          />
                        </Box>
                      )
                    })}
                  </Flex>
                </Flex>
              </Flex>
            </GridItem>
          </SimpleGrid>
        </Container>
      </Box>
    </Flex>
  )
}
